/**
 * Wraps a promise in setTimeout. Will reject if the given promise doesn't resolve before the timeout.
 * @param promise
 * @param timeout
 * @param error optional rejection reason
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function timeoutPromise<T>(promise: Promise<T>, timeout: number, error?: string | Error): Promise<T> {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      reject(error);
    }, timeout);
    promise.then(resolve, reject);
  });
}

export interface PromiseResult<T> {
  status: 'resolved' | 'rejected';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value?: T;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  reason?: any;
}

/**
 * Simple (and probably incomplete) implementation of Promise.allSettled
 * (https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Promise/allSettled).
 *
 * @param promises array of promises
 * @return {Promise<PromiseResult>[]} Promise that resolves to an array of PromiseResult objects
 */
export async function allSettled<T>(promises: Promise<T>[]): Promise<PromiseResult<T>[]> {
  return await Promise.all(
    promises.map((promise) => promise
      .then(
        (value) => {
          const ret: PromiseResult<T> = {
            status: 'resolved',
            value
          }
          return ret;
        },
        (reason) => {
          const ret: PromiseResult<T> = {
            status: 'rejected',
            reason
          };
          return ret;
        }
      ))
  );
}
